import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { formatPrice, getCurrency, initializeCurrency, convertCurrency, formatCurrency } from './utils/currency';
import './BookingSuccessPage.css';
import jsPDF from 'jspdf';

/** Raw Duffel order on the booking payload (preferred) or the booking object itself. */
function getRawDuffelOrder(booking) {
  if (!booking) return null;
  return booking.rawOrder || booking;
}

function isDuffelOrderId(value) {
  return typeof value === 'string' && value.startsWith('ord_');
}

/** Airline PNR / record locator from Duffel (`booking_reference`). */
function getAirlinePnr(booking) {
  const ref = getRawDuffelOrder(booking)?.booking_reference;
  if (!ref || !String(ref).trim()) return null;
  return String(ref).trim();
}

function getDuffelOrderId(booking) {
  return booking?.duffelOrderId || getRawDuffelOrder(booking)?.id || null;
}

/** Per-carrier PNRs when Duffel returns `booking_references` (e.g. interline). */
function getBookingReferencesList(booking) {
  const list = getRawDuffelOrder(booking)?.booking_references;
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (typeof item === 'string') {
        const s = item.trim();
        return s ? { reference: s, carrier: null } : null;
      }
      const reference =
        item?.booking_reference ||
        item?.reference ||
        item?.carrier_reference;
      if (!reference) return null;
      const carrier = item?.carrier?.name || item?.carrier?.iata_code || null;
      return { reference: String(reference).trim(), carrier };
    })
    .filter(Boolean);
}

/** E-ticket / document numbers from Duffel `documents` (when issued). */
function getDuffelTicketDocuments(booking) {
  const raw = getRawDuffelOrder(booking);
  const docs = raw?.documents;
  if (!Array.isArray(docs)) return [];
  const passengers = raw?.passengers || booking?.passengers || [];
  const passengerById = {};
  for (const p of passengers) {
    if (p?.id) passengerById[p.id] = p;
  }
  const rows = [];
  for (const doc of docs) {
    const type = String(doc?.type || '').toLowerCase();
    if (type === 'passport' || type === 'passport_card') continue;
    const id =
      doc?.unique_identifier ||
      doc?.uniqueIdentifier ||
      doc?.ticket_number ||
      doc?.ticketNumber;
    if (!id) continue;
    const pids = doc.passenger_ids || doc.passengerIds || [];
    const names = [];
    for (const pid of pids) {
      const p = passengerById[pid];
      if (p) {
        names.push(
          [p.given_name, p.family_name].filter(Boolean).join(' ').trim()
        );
      }
    }
    rows.push({
      ticketNumber: String(id),
      documentType: doc.type || 'travel_document',
      passengersLabel: names.filter(Boolean).join(', ') || null,
    });
  }
  return rows;
}

const BookingSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSignedIn, setIsSignedIn] = React.useState(false);
  
  // Get booking data from navigation state - REQUIRED, no fallback
  const booking = location.state?.booking;
  const payment = location.state?.payment;
  const flight = location.state?.flight;
  const passengerData = location.state?.passengerData || {};

  // Check if user is signed in
  React.useEffect(() => {
    const authStatus = localStorage.getItem('isSignedIn') === 'true';
    setIsSignedIn(authStatus);
  }, []);

  // Listen for auth status changes
  React.useEffect(() => {
    const handleStorageChange = () => {
      const authStatus = localStorage.getItem('isSignedIn') === 'true';
      setIsSignedIn(authStatus);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authStatusChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authStatusChange', handleStorageChange);
    };
  }, []);

  // Get error message if booking failed but payment succeeded
  const errorMessage = location.state?.error;
  const successMessage = location.state?.message;
  const [currency, setCurrency] = useState(null); // Currency based on location

  // Initialize currency on component mount
  useEffect(() => {
    const initCurrency = async () => {
      try {
        console.log('BookingSuccessPage: Initializing currency...');
        const detectedCurrency = await initializeCurrency(true);
        setCurrency(detectedCurrency);
        console.log('BookingSuccessPage: Currency initialized:', {
          code: detectedCurrency.code,
          symbol: detectedCurrency.symbol,
          name: detectedCurrency.name
        });
      } catch (error) {
        console.error('BookingSuccessPage: Error initializing currency:', error);
        const fallbackCurrency = getCurrency();
        setCurrency(fallbackCurrency);
      }
    };
    initCurrency();

    // Listen for currency initialization from App
    const handleCurrencyInit = (event) => {
      console.log('BookingSuccessPage: Received currency from App:', event.detail);
      setCurrency(event.detail);
    };
    
    window.addEventListener('currencyInitialized', handleCurrencyInit);
    return () => {
      window.removeEventListener('currencyInitialized', handleCurrencyInit);
    };
  }, []);

  // Redirect if no booking data AND no payment data (complete failure)
  React.useEffect(() => {
    if ((!booking && !payment) || !flight) {
      console.error('No booking or flight data found. Redirecting to home...');
      navigate('/', { 
        replace: true,
        state: { error: 'No booking information available' }
      });
    }
  }, [booking, payment, flight, navigate]);

  const additionalOptions = location.state?.additionalOptions || {};

  // Get booking reference from actual booking data
  const bookingReference = booking?.bookingReference || booking?.id || booking?.duffelOrderId || 'N/A';
  const airlinePnr = getAirlinePnr(booking);
  const duffelOrderId = getDuffelOrderId(booking);
  const bookingRefsList = getBookingReferencesList(booking);
  const ticketDocuments = getDuffelTicketDocuments(booking);
  const extraCarrierPnrs = bookingRefsList.filter(
    (row) => row.reference && row.reference !== airlinePnr
  );
  const primaryConfirmationCode = airlinePnr || bookingReference;
  const confirmationLabel = airlinePnr
    ? 'PNR / Record locator'
    : isDuffelOrderId(bookingReference)
      ? 'Order ID'
      : 'Booking reference';
  const showDuffelOrderSub =
    airlinePnr &&
    duffelOrderId &&
    duffelOrderId !== airlinePnr &&
    isDuffelOrderId(duffelOrderId);

  // Extract real flight data from booking slices
  const allSlices = booking?.slices || [];
  const firstSlice = allSlices[0];
  const returnSlice = allSlices[1] || null;
  const firstSegment = firstSlice?.segments?.[0];
  const lastSegment = firstSlice?.segments?.[firstSlice.segments.length - 1];
  
  // Get return flight segments if exists
  const returnFirstSegment = returnSlice?.segments?.[0];
  const returnLastSegment = returnSlice?.segments?.[returnSlice?.segments?.length - 1];
  
  // Get real passenger data from booking
  const bookingPassengers = booking?.passengers || [];
  const firstPassenger = bookingPassengers[0] || passengerData;
  
  // Get conditions/policies from booking
  const conditions = booking?.conditions || booking?.rawOrder?.conditions;

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Format time helper
  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  // Get airline name from segment
  const getAirlineName = (carrierCode) => {
    if (!carrierCode) return flight?.airline || 'Airline';
    // You can expand this with a full airline code mapping
    const airlines = {
      'BA': 'British Airways',
      'EK': 'Emirates',
      'LH': 'Lufthansa',
      'AF': 'Air France',
      'KL': 'KLM',
      'VS': 'Virgin Atlantic',
      'QR': 'Qatar Airways',
      'TK': 'Turkish Airlines'
    };
    return airlines[carrierCode] || carrierCode;
  };

  // Calculate total - use real booking amount
  const calculateTotal = () => {
    if (booking?.totalAmount) {
      return `₦${parseFloat(booking.totalAmount).toLocaleString()}`;
    }
    // Fallback to flight price if booking amount not available
    const flightTotal = flight?.price?.total || flight?.total || flight?.price || '0';
    let total = parseFloat(String(flightTotal).replace(/[₦,]/g, '')) || 0;
    if (additionalOptions.extraBaggage) total += 15000;
    if (additionalOptions.seatSelection) total += 5000;
    if (additionalOptions.tripInsurance) total += 5000;
    return `₦${total.toLocaleString()}`;
  };

  // Get total paid in NGN (numeric) so we can display it in the user's local currency
  const getTotalPaidAmountNGN = () => {
    // Prefer converted total amount stored on booking (already NGN)
    if (booking?.totalAmount) {
      return parseFloat(booking.totalAmount) || 0;
    }

    // If we have rawOrder amounts, convert from original currency to NGN
    if (booking?.rawOrder?.total_amount) {
      const originalCurrency =
        booking.rawOrder.total_currency ||
        booking.rawOrder.base_currency ||
        'GBP';
      const rawTotal = parseFloat(booking.rawOrder.total_amount) || 0;
      return convertCurrency(rawTotal, originalCurrency, 'NGN') || 0;
    }

    // Fallback: parse from flight price/total strings (assumed NGN)
    const flightTotal =
      flight?.price?.total || flight?.total || flight?.price || '0';
    let total =
      parseFloat(String(flightTotal).replace(/[^\d.]/g, '')) || 0;

    // Add extras in NGN
    if (additionalOptions.extraBaggage) total += 15000;
    if (additionalOptions.seatSelection) total += 5000;
    if (additionalOptions.tripInsurance) total += 5000;

    return total;
  };

  // Helper function to format date for ticket (e.g., "Fri, 6 Feb 2026")
  const formatTicketDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Helper function to get baggage info from conditions
  const getBaggageInfo = (slice) => {
    const baggageInfo = {
      carryOn: 'Not specified',
      checked: 'Not specified'
    };
    
    // Check conditions for baggage allowance
    if (conditions?.baggage) {
      if (conditions.baggage.carry_on) {
        baggageInfo.carryOn = `${conditions.baggage.carry_on.quantity || 1} carry-on bag${(conditions.baggage.carry_on.quantity || 1) > 1 ? 's' : ''}`;
      }
      if (conditions.baggage.checked) {
        baggageInfo.checked = `${conditions.baggage.checked.quantity || 1} checked bag${(conditions.baggage.checked.quantity || 1) > 1 ? 's' : ''}`;
      }
    }
    
    // Default values if not found
    if (baggageInfo.carryOn === 'Not specified') baggageInfo.carryOn = '1 carry-on bag';
    if (baggageInfo.checked === 'Not specified') baggageInfo.checked = '1 checked bag';
    
    return baggageInfo;
  };

  const handleDownloadTicket = () => {
    try {
      console.log('Generating beautiful ticket PDF for booking:', bookingReference);
      
      // Use detected currency for PDF amounts, fall back to current global currency if needed
      const userCurrencyForPdf = currency || getCurrency();
      
      // Create new PDF document
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 0;
      
      // Color scheme
      const primaryColor = [1, 119, 52]; // Green
      const secondaryColor = [2, 150, 65]; // Light Green
      const accentColor = [46, 204, 113]; // Green
      const darkGray = [44, 62, 80];
      const lightGray = [236, 240, 241];
      const borderGray = [189, 195, 199];
      
      // Helper function to add text with wrapping
      const addText = (text, x, y, fontSize = 10, isBold = false, align = 'left', maxWidth = null, color = null) => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');
        if (color) {
          doc.setTextColor(color[0], color[1], color[2]);
        }
        const width = maxWidth || (pageWidth - x - 20);
        const lines = doc.splitTextToSize(String(text || ''), width);
        doc.text(lines, x, y, { align });
        doc.setTextColor(0, 0, 0); // Reset to black
        return y + (lines.length * fontSize * 0.4);
      };
      
      // Helper to add a box/card
      const addBox = (x, y, width, height, fillColor = null, strokeColor = null) => {
        if (fillColor) {
          doc.setFillColor(fillColor[0], fillColor[1], fillColor[2]);
          doc.rect(x, y, width, height, 'F');
        }
        if (strokeColor) {
          doc.setDrawColor(strokeColor[0], strokeColor[1], strokeColor[2]);
          doc.setLineWidth(0.5);
          doc.rect(x, y, width, height, 'S');
        }
      };
      
      // Helper to add a line
      const addLine = (x1, y1, x2, y2, color = borderGray, width = 0.5) => {
        doc.setDrawColor(color[0], color[1], color[2]);
        doc.setLineWidth(width);
        doc.line(x1, y1, x2, y2);
      };
      
      // Helper to check if new page needed
      const checkNewPage = (requiredSpace = 20) => {
        if (yPosition > pageHeight - requiredSpace) {
          doc.addPage();
          yPosition = 20; // Add top margin for new pages (same as first page)
          return true;
        }
        return false;
      };
      
      // Beautiful Header Section with gradient effect
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 50, 'F');
      
      // Add decorative border
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(2);
      doc.line(0, 50, pageWidth, 50);
      
      // Title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('FLIGHT TICKET', pageWidth / 2, 25, { align: 'center' });
      
      // Subtitle
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Electronic Travel Document', pageWidth / 2, 35, { align: 'center' });
      
      // PNR / booking confirmation — top right (matches success page)
      if (primaryConfirmationCode && primaryConfirmationCode !== 'N/A') {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`${confirmationLabel}:`, pageWidth - 25, 18, { align: 'right' });
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(238, 196, 57);
        const refLines = doc.splitTextToSize(String(primaryConfirmationCode), 72);
        doc.text(refLines, pageWidth - 25, 26, { align: 'right' });
        doc.setTextColor(255, 255, 255);
      }
      
      doc.setTextColor(0, 0, 0);
      yPosition = 60;
      
      // PNR / primary confirmation (Duffel `booking_reference` when present)
      if (primaryConfirmationCode && primaryConfirmationCode !== 'N/A') {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text(`${confirmationLabel.toUpperCase()}:`, 20, yPosition);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(primaryConfirmationCode, 20, yPosition + 8);
        doc.setTextColor(0, 0, 0);
        yPosition += 18;
      }

      if (showDuffelOrderSub && duffelOrderId) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text('DUFFEL ORDER ID:', 20, yPosition);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(duffelOrderId, 20, yPosition + 7);
        yPosition += 16;
      }

      if (extraCarrierPnrs.length > 0) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text('ADDITIONAL CARRIER REFERENCES:', 20, yPosition);
        yPosition += 7;
        doc.setFont('helvetica', 'normal');
        extraCarrierPnrs.forEach((row) => {
          const line = row.carrier
            ? `${row.carrier}: ${row.reference}`
            : row.reference;
          doc.text(line, 22, yPosition);
          yPosition += 6;
        });
        yPosition += 6;
      }

      if (ticketDocuments.length > 0) {
        checkNewPage(40);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text('TICKET / DOCUMENT NUMBERS:', 20, yPosition);
        yPosition += 7;
        doc.setFont('helvetica', 'normal');
        ticketDocuments.forEach((row) => {
          checkNewPage(18);
          const who = row.passengersLabel ? ` — ${row.passengersLabel}` : '';
          const typeLabel = row.documentType ? ` (${row.documentType})` : '';
          const line = `${row.ticketNumber}${typeLabel}${who}`;
          const wrapped = doc.splitTextToSize(line, pageWidth - 44);
          doc.text(wrapped, 22, yPosition);
          yPosition += wrapped.length * 5 + 2;
        });
        yPosition += 6;
      }

      if (!airlinePnr && isDuffelOrderId(bookingReference)) {
        checkNewPage(22);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        const hintLines = doc.splitTextToSize(
          'Airline PNR (record locator) was not returned yet. It often appears after the carrier syncs; check back or use the airline website with the details above.',
          pageWidth - 40
        );
        doc.text(hintLines, 20, yPosition);
        yPosition += hintLines.length * 4 + 8;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
      }
      
      // Booking Status
      if (booking?.status) {
        addText('STATUS:', 20, yPosition, 10, true);
        addText(booking.status.toUpperCase(), 60, yPosition, 10, false);
        yPosition += 10;
      }
      
      // Booking Date
      if (booking?.created_at) {
        const bookingDate = new Date(booking.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        addText('BOOKED ON:', 20, yPosition, 10, true);
        addText(bookingDate, 60, yPosition, 10, false);
        yPosition += 15;
      }
      
      // JOURNEY DETAILS SECTION - Handle multi-city or single/round-trip
      // For multi-city (3+ slices), generate all routes
      // For round-trip (2 slices), show Outbound and Return
      // For one-way (1 slice), show Outbound only
      
      if (allSlices.length > 2) {
        // Multi-city: Generate all routes
        allSlices.forEach((slice, sliceIndex) => {
          checkNewPage(100);
          
          // Section header with colored background
          addBox(15, yPosition, pageWidth - 30, 12, secondaryColor, null);
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text(`ROUTE ${sliceIndex + 1} OF ${allSlices.length}`, 25, yPosition + 8);
          doc.setTextColor(0, 0, 0);
          yPosition += 18;
          
          // Flight details card
          addBox(20, yPosition, pageWidth - 40, 0, null, borderGray);
          const cardStartY = yPosition;
          yPosition += 10;
          
          const seg = slice.segments?.[0];
          const lastSeg = slice.segments?.[slice.segments.length - 1];
          
          if (seg && lastSeg) {
            const airlineName = seg?.marketing_carrier?.name || 
                                getAirlineName(seg?.marketing_carrier?.iata_code) || 
                                'Airline';
            const flightNumber = seg?.marketing_carrier_flight_number || '';
            const carrierCode = seg?.marketing_carrier?.iata_code || '';
            const originCode = seg?.origin?.iata_code || 'N/A';
            const destCode = lastSeg?.destination?.iata_code || 'N/A';
            const originName = seg?.origin?.name || seg?.origin?.city_name || '';
            const destName = lastSeg?.destination?.name || lastSeg?.destination?.city_name || '';
            const originTerminal = seg?.origin_terminal || '';
            const destTerminal = lastSeg?.destination_terminal || '';
            const aircraft = seg?.aircraft?.name || '';
            const serviceType = slice?.fare_brand_name || slice?.cabin_class_name || 'Basic';
            const stops = slice.segments.length - 1;
            const isNonStop = stops === 0;
            
            // Airline and Flight Number
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
            doc.text(airlineName, 25, yPosition);
            yPosition += 8;
            
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
            doc.text(`Flight ${carrierCode}${flightNumber}`, 25, yPosition);
            yPosition += 10;
            
            // Route
            const routeY = yPosition;
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(originCode, 25, routeY);
            doc.setFontSize(12);
            doc.text('-', pageWidth / 2, routeY);
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.text(destCode, pageWidth - 45, routeY);
            yPosition += 12;
            
            // Times
            const depDate = formatTicketDate(seg.departing_at);
            const depTime = formatTime(seg.departing_at);
            const arrDate = formatTicketDate(lastSeg.arriving_at);
            const arrTime = formatTime(lastSeg.arriving_at);
            const nextDay = new Date(lastSeg.arriving_at).getDate() !== new Date(seg.departing_at).getDate();
            
            // Departure
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
            doc.text('DEPARTURE', 25, yPosition);
            yPosition += 6;
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(depTime, 25, yPosition);
            yPosition += 6;
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
            doc.text(depDate, 25, yPosition);
            yPosition += 5;
            doc.setFontSize(9);
            const originText = `${originName} (${originCode})${originTerminal ? `, T${originTerminal}` : ''}`;
            const originLines = doc.splitTextToSize(originText, (pageWidth / 2) - 30);
            doc.text(originLines, 25, yPosition);
            const originHeight = Math.max(originLines.length * 4, 4);
            
            // Duration
            const durationY = yPosition;
            if (slice?.duration) {
              let durationText = '';
              if (typeof slice.duration === 'number') {
                const hours = Math.floor(slice.duration / 60);
                const minutes = slice.duration % 60;
                durationText = `${hours}h ${String(minutes).padStart(2, '0')}m`;
              } else if (typeof slice.duration === 'string' && slice.duration.startsWith('PT')) {
                const match = slice.duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
                if (match) {
                  const hours = match[1] || '0';
                  const minutes = match[2] || '0';
                  if (hours === '0') durationText = `${minutes}m`;
                  else if (minutes === '0') durationText = `${hours}h`;
                  else durationText = `${hours}h ${minutes}m`;
                }
              }
              if (durationText) {
                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
                doc.text(durationText, pageWidth / 2, durationY, { align: 'center' });
                doc.text(isNonStop ? 'Non-stop' : `${stops} stop${stops > 1 ? 's' : ''}`, pageWidth / 2, durationY + 4, { align: 'center' });
              }
            }
            
            // Arrival
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
            doc.text('ARRIVAL', pageWidth - 25, yPosition, { align: 'right' });
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(arrTime + (nextDay ? '+1' : ''), pageWidth - 25, yPosition + 5, { align: 'right' });
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
            doc.text(arrDate + (nextDay ? ' (+1)' : ''), pageWidth - 25, yPosition + 9, { align: 'right' });
            doc.setFontSize(9);
            const destText = `${destName} (${destCode})${destTerminal ? `, T${destTerminal}` : ''}`;
            const destLines = doc.splitTextToSize(destText, (pageWidth / 2) - 30);
            destLines.forEach((line, idx) => {
              doc.text(line, pageWidth - 25, yPosition + 13 + (idx * 4), { align: 'right' });
            });
            
            yPosition += Math.max(originHeight, destLines.length * 4 + 13, 18);
            
            // Flight details
            addLine(25, yPosition, pageWidth - 25, yPosition, borderGray);
            yPosition += 6;
            
            const details = [];
            const cabinClass = slice?.cabin_class_name || slice?.cabin_class || 'Economy';
            details.push(`Cabin: ${cabinClass}`);
            if (aircraft) details.push(`Aircraft: ${aircraft}`);
            const baggage = getBaggageInfo(slice);
            details.push(`Baggage: ${baggage.carryOn}, ${baggage.checked}`);
            
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
            const detailsText = details.join(' • ');
            const detailsLines = doc.splitTextToSize(detailsText, pageWidth - 50);
            doc.text(detailsLines, 25, yPosition);
            yPosition += (detailsLines.length * 4) + 8;
            
            // Close the card
            const cardHeight = yPosition - cardStartY;
            addBox(20, cardStartY, pageWidth - 40, cardHeight, null, borderGray);
            yPosition += 5;
          }
        });
      } else {
        // One-way or Round-trip: Show Outbound Flight
        checkNewPage(100);
        
        // Section header with colored background
        addBox(15, yPosition, pageWidth - 30, 12, secondaryColor, null);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('OUTBOUND FLIGHT', 25, yPosition + 8);
        doc.setTextColor(0, 0, 0);
        yPosition += 18;
        
        // Flight details card
        addBox(20, yPosition, pageWidth - 40, 0, null, borderGray);
        const cardStartY = yPosition;
        yPosition += 10;
        
        if (firstSlice && firstSegment) {
        const airlineName = firstSegment?.marketing_carrier?.name || 
                            getAirlineName(firstSegment?.marketing_carrier?.iata_code) || 
                            'Airline';
        const flightNumber = firstSegment?.marketing_carrier_flight_number || '';
        const carrierCode = firstSegment?.marketing_carrier?.iata_code || '';
        const originCode = firstSegment?.origin?.iata_code || 'N/A';
        const destCode = lastSegment?.destination?.iata_code || 'N/A';
        const originName = firstSegment?.origin?.name || firstSegment?.origin?.city_name || '';
        const destName = lastSegment?.destination?.name || lastSegment?.destination?.city_name || '';
        const originTerminal = firstSegment?.origin_terminal || '';
        const destTerminal = lastSegment?.destination_terminal || '';
        const aircraft = firstSegment?.aircraft?.name || '';
        const serviceType = firstSlice?.fare_brand_name || firstSlice?.cabin_class_name || 'Basic';
        const stops = firstSlice.segments.length - 1;
        const isNonStop = stops === 0;
        
        // Airline and Flight Number - Large and prominent
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(airlineName, 25, yPosition);
        yPosition += 8;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text(`Flight ${carrierCode}${flightNumber}`, 25, yPosition);
        yPosition += 10;
        
        // Route with large airport codes
        const routeY = yPosition;
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(originCode, 25, routeY);
        
        // Arrow
        doc.setFontSize(12);
        doc.text('-', pageWidth / 2, routeY);
        
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text(destCode, pageWidth - 45, routeY);
        yPosition += 12;
        
        // Times - Side by side layout
        const depDate = formatTicketDate(firstSegment.departing_at);
        const depTime = formatTime(firstSegment.departing_at);
        const arrDate = formatTicketDate(lastSegment.arriving_at);
        const arrTime = formatTime(lastSegment.arriving_at);
        const nextDay = new Date(lastSegment.arriving_at).getDate() !== new Date(firstSegment.departing_at).getDate();
        
        // Departure column
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text('DEPARTURE', 25, yPosition);
        yPosition += 6;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(depTime, 25, yPosition);
        yPosition += 6;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text(depDate, 25, yPosition);
        yPosition += 5;
        doc.setFontSize(9);
        const originText = `${originName} (${originCode})${originTerminal ? `, T${originTerminal}` : ''}`;
        const originLines = doc.splitTextToSize(originText, (pageWidth / 2) - 30);
        doc.text(originLines, 25, yPosition);
        const originHeight = Math.max(originLines.length * 4, 4);
        
        // Duration in center
        const durationY = yPosition;
        if (firstSlice?.duration && !isNaN(firstSlice.duration)) {
          const durationMinutes = parseInt(firstSlice.duration) || 0;
          const hours = Math.floor(durationMinutes / 60);
          const minutes = durationMinutes % 60;
          if (hours >= 0 && minutes >= 0) {
            const durationText = `${hours}h ${String(minutes).padStart(2, '0')}m`;
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
            doc.text(durationText, pageWidth / 2, durationY, { align: 'center' });
            doc.text(isNonStop ? 'Non-stop' : `${stops} stop${stops > 1 ? 's' : ''}`, pageWidth / 2, durationY + 4, { align: 'center' });
          }
        }
        
        // Arrival column - align with departure
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text('ARRIVAL', pageWidth - 25, yPosition, { align: 'right' });
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(arrTime + (nextDay ? '+1' : ''), pageWidth - 25, yPosition + 5, { align: 'right' });
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text(arrDate + (nextDay ? ' (+1)' : ''), pageWidth - 25, yPosition + 9, { align: 'right' });
        doc.setFontSize(9);
        const destText = `${destName} (${destCode})${destTerminal ? `, T${destTerminal}` : ''}`;
        const destLines = doc.splitTextToSize(destText, (pageWidth / 2) - 30);
        destLines.forEach((line, idx) => {
          doc.text(line, pageWidth - 25, yPosition + 13 + (idx * 4), { align: 'right' });
        });
        
        yPosition += Math.max(originHeight, destLines.length * 4 + 13, 18);
        
        // Flight details row
        addLine(25, yPosition, pageWidth - 25, yPosition, borderGray);
        yPosition += 6;
        
        const details = [];
        const cabinClass = firstSlice?.cabin_class_name || firstSlice?.cabin_class || 'Economy';
        details.push(`Cabin: ${cabinClass}`);
        if (aircraft) details.push(`Aircraft: ${aircraft}`);
        const baggage = getBaggageInfo(firstSlice);
        details.push(`Baggage: ${baggage.carryOn}, ${baggage.checked}`);
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        const detailsText = details.join(' • ');
        const detailsLines = doc.splitTextToSize(detailsText, pageWidth - 50);
        doc.text(detailsLines, 25, yPosition);
        yPosition += (detailsLines.length * 4) + 8;
        
        // Close the card
        const cardHeight = yPosition - cardStartY;
        addBox(20, cardStartY, pageWidth - 40, cardHeight, null, borderGray);
        yPosition += 5;
        }
      }
      
      // RETURN FLIGHT (if round-trip, NOT multi-city) - Same beautiful design
      // Only show "RETURN FLIGHT" if there are exactly 2 slices (round-trip)
      // For multi-city (3+ slices), each route is handled separately above
      if (returnSlice && returnFirstSegment && allSlices.length === 2) {
        checkNewPage(100);
        
        // Section header
        addBox(15, yPosition, pageWidth - 30, 12, secondaryColor, null);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('RETURN FLIGHT', 25, yPosition + 8);
        doc.setTextColor(0, 0, 0);
        yPosition += 18;
        
        // Flight details card
        addBox(20, yPosition, pageWidth - 40, 0, null, borderGray);
        const returnCardStartY = yPosition;
        yPosition += 10;
        
        const airlineName = returnFirstSegment?.marketing_carrier?.name || 
                            getAirlineName(returnFirstSegment?.marketing_carrier?.iata_code) || 
                            'Airline';
        const flightNumber = returnFirstSegment?.marketing_carrier_flight_number || '';
        const carrierCode = returnFirstSegment?.marketing_carrier?.iata_code || '';
        const originCode = returnFirstSegment?.origin?.iata_code || 'N/A';
        const destCode = returnLastSegment?.destination?.iata_code || 'N/A';
        const originName = returnFirstSegment?.origin?.name || returnFirstSegment?.origin?.city_name || '';
        const destName = returnLastSegment?.destination?.name || returnLastSegment?.destination?.city_name || '';
        const originTerminal = returnFirstSegment?.origin_terminal || '';
        const destTerminal = returnLastSegment?.destination_terminal || '';
        const aircraft = returnFirstSegment?.aircraft?.name || '';
        const serviceType = returnSlice?.fare_brand_name || returnSlice?.cabin_class_name || 'Basic';
        const stops = returnSlice.segments.length - 1;
        const isNonStop = stops === 0;
        
        // Same beautiful layout as outbound
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(airlineName, 25, yPosition);
        yPosition += 8;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text(`Flight ${carrierCode}${flightNumber}`, 25, yPosition);
        yPosition += 10;
        
        // Route
        const returnRouteY = yPosition;
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(originCode, 25, returnRouteY);
        doc.setFontSize(12);
        doc.text('-', pageWidth / 2, returnRouteY);
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text(destCode, pageWidth - 45, returnRouteY);
        yPosition += 12;
        
        // Times
        const returnDepDate = formatTicketDate(returnFirstSegment.departing_at);
        const returnDepTime = formatTime(returnFirstSegment.departing_at);
        const returnArrDate = formatTicketDate(returnLastSegment.arriving_at);
        const returnArrTime = formatTime(returnLastSegment.arriving_at);
        const returnNextDay = new Date(returnLastSegment.arriving_at).getDate() !== new Date(returnFirstSegment.departing_at).getDate();
        
        // Departure
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text('DEPARTURE', 25, yPosition);
        yPosition += 6;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(returnDepTime, 25, yPosition);
        yPosition += 6;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text(returnDepDate, 25, yPosition);
        yPosition += 5;
        doc.setFontSize(9);
        doc.text(`${originName} (${originCode})${originTerminal ? `, T${originTerminal}` : ''}`, 25, yPosition);
        
        // Duration
        if (returnSlice?.duration && !isNaN(returnSlice.duration)) {
          const durationMinutes = parseInt(returnSlice.duration) || 0;
          const hours = Math.floor(durationMinutes / 60);
          const minutes = durationMinutes % 60;
          if (hours >= 0 && minutes >= 0) {
            const durationText = `${hours}h ${String(minutes).padStart(2, '0')}m`;
            const durationY = yPosition - 8;
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
            doc.text(durationText, pageWidth / 2, durationY, { align: 'center' });
            doc.text(isNonStop ? 'Non-stop' : `${stops} stop${stops > 1 ? 's' : ''}`, pageWidth / 2, durationY + 5, { align: 'center' });
          }
        }
        
        // Arrival
        const returnArrivalStartY = yPosition - 11;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text('ARRIVAL', pageWidth - 25, returnArrivalStartY, { align: 'right' });
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(returnArrTime + (returnNextDay ? '+1' : ''), pageWidth - 25, returnArrivalStartY + 6, { align: 'right' });
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text(returnArrDate + (returnNextDay ? ' (+1)' : ''), pageWidth - 25, returnArrivalStartY + 12, { align: 'right' });
        doc.setFontSize(9);
        doc.text(`${destName} (${destCode})${destTerminal ? `, T${destTerminal}` : ''}`, pageWidth - 25, returnArrivalStartY + 18, { align: 'right' });
        
        yPosition += 15;
        
        // Details
        addLine(25, yPosition, pageWidth - 25, yPosition, borderGray);
        yPosition += 8;
        
        const returnDetails = [];
        const returnCabinClass = returnSlice?.cabin_class_name || returnSlice?.cabin_class || 'Economy';
        returnDetails.push(`Cabin: ${returnCabinClass}`);
        if (aircraft) returnDetails.push(`Aircraft: ${aircraft}`);
        const returnBaggage = getBaggageInfo(returnSlice);
        returnDetails.push(`Baggage: ${returnBaggage.carryOn}, ${returnBaggage.checked}`);
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        const returnDetailsText = returnDetails.join(' • ');
        const returnDetailsLines = doc.splitTextToSize(returnDetailsText, pageWidth - 50);
        doc.text(returnDetailsLines, 25, yPosition);
        yPosition += (returnDetailsLines.length * 4) + 8;
        
        // Close card
        const returnCardHeight = yPosition - returnCardStartY;
        addBox(20, returnCardStartY, pageWidth - 40, returnCardHeight, null, borderGray);
        yPosition += 5;
      }
      
      // PASSENGER INFORMATION - Beautiful card design
      checkNewPage(80);
      
      addBox(15, yPosition, pageWidth - 30, 12, secondaryColor, null);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('PASSENGER INFORMATION', 25, yPosition + 8);
      doc.setTextColor(0, 0, 0);
      yPosition += 18;
      
      bookingPassengers.forEach((passenger, index) => {
        checkNewPage(50);
        
        const passengerName = passenger.given_name && passenger.family_name
          ? `${passenger.given_name} ${passenger.family_name}`
          : passengerData.fullName || 
            (passengerData.firstName && passengerData.lastName 
              ? `${passengerData.firstName} ${passengerData.lastName}` 
              : 'Passenger');
        
        const title = passenger.title ? passenger.title.toUpperCase() : '';
        const fullName = title ? `${title} ${passengerName}` : passengerName;
        
        // Passenger card
        const passengerCardY = yPosition;
        addBox(20, yPosition, pageWidth - 40, 0, lightGray, borderGray);
        yPosition += 8;
        
        // Passenger header
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(`Passenger ${index + 1} - ${passenger.type.toUpperCase()}`, 25, yPosition);
        yPosition += 8;
        
        // Name
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(fullName, 25, yPosition);
        yPosition += 8;
        
        // Details in two columns
        const detailsLeft = [];
        const detailsRight = [];
        
        if (passenger.born_on) {
          const dob = new Date(passenger.born_on);
          const dobFormatted = `${String(dob.getDate()).padStart(2, '0')}/${String(dob.getMonth() + 1).padStart(2, '0')}/${dob.getFullYear()}`;
          detailsLeft.push(`DOB: ${dobFormatted}`);
        }
        
        if (passenger.gender) {
          const gender = passenger.gender === 'm' ? 'Male' : passenger.gender === 'f' ? 'Female' : passenger.gender;
          detailsLeft.push(`Gender: ${gender}`);
        }
        
        if (passenger.email) {
          detailsRight.push(`Email: ${passenger.email}`);
        }
        
        if (passenger.phone_number) {
          detailsRight.push(`Phone: ${passenger.phone_number}`);
        }
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        
        // Left column - DOB and Gender
        let leftY = yPosition;
        if (detailsLeft.length > 0) {
          const leftText = detailsLeft.join(' • ');
          const leftLines = doc.splitTextToSize(leftText, (pageWidth / 2) - 30);
          doc.text(leftLines, 25, leftY);
          leftY += leftLines.length * 4;
        }
        
        // Right column - Email and Phone on separate lines
        let rightY = yPosition;
        if (passenger.email) {
          doc.text(`Email: ${passenger.email}`, pageWidth / 2 + 10, rightY);
          rightY += 5;
        }
        if (passenger.phone_number) {
          doc.text(`Phone: ${passenger.phone_number}`, pageWidth / 2 + 10, rightY);
          rightY += 5;
        }
        
        // Update yPosition based on the tallest column
        yPosition += Math.max(leftY - yPosition, rightY - yPosition) + 5;
        
        // Close passenger card
        const passengerCardHeight = yPosition - passengerCardY;
        addBox(20, passengerCardY, pageWidth - 40, passengerCardHeight, null, borderGray);
        yPosition += 5;
      });
      
      // FLIGHT INFORMATION (Baggage summary) - Beautiful design
      checkNewPage(60);
      addBox(15, yPosition, pageWidth - 30, 12, secondaryColor, null);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('BAGGAGE INFORMATION', 25, yPosition + 8);
      doc.setTextColor(0, 0, 0);
      yPosition += 18;
      
      allSlices.forEach((slice, sliceIndex) => {
        checkNewPage(40);
        const segment = slice.segments[0];
        const lastSeg = slice.segments[slice.segments.length - 1];
        const originCode = segment?.origin?.iata_code || 'N/A';
        const destCode = lastSeg?.destination?.iata_code || 'N/A';
        const depDate = formatTicketDate(segment?.departing_at);
        const depTime = formatTime(segment?.departing_at);
        const airlineName = segment?.marketing_carrier?.name || getAirlineName(segment?.marketing_carrier?.iata_code) || 'Airline';
        const baggage = getBaggageInfo(slice);
        
        // Baggage card
        addBox(20, yPosition, pageWidth - 40, 0, lightGray, borderGray);
        const baggageCardY = yPosition;
        yPosition += 8;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(`${airlineName} - ${originCode} to ${destCode}`, 25, yPosition);
        yPosition += 6;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text(`Flight Date: ${depDate} at ${depTime}`, 25, yPosition);
        yPosition += 8;
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text(`${baggage.checked}`, 25, yPosition);
        yPosition += 6;
        doc.text(`${baggage.carryOn}`, 25, yPosition);
        yPosition += 10;
        
        // Close baggage card
        const baggageCardHeight = yPosition - baggageCardY;
        addBox(20, baggageCardY, pageWidth - 40, baggageCardHeight, null, borderGray);
        yPosition += 5;
      });
      
      // POLICIES SECTION - Beautiful design
      checkNewPage(100);
      addBox(15, yPosition, pageWidth - 30, 12, secondaryColor, null);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('BOOKING POLICIES', 25, yPosition + 8);
      doc.setTextColor(0, 0, 0);
      yPosition += 18;
      
      // Policies card
      addBox(20, yPosition, pageWidth - 40, 0, null, borderGray);
      const policiesCardY = yPosition;
      yPosition += 10;
      
      // Flight change policy
      if (conditions?.change_before_departure) {
        const changePolicy = conditions.change_before_departure;
        let penalty = 'a change penalty';
        if (changePolicy.penalty_amount) {
          const penaltyBase =
            parseFloat(changePolicy.penalty_amount) || 0;
          if (penaltyBase > 0) {
            // Convert original penalty (in booking currency) → NGN → user currency
            const penaltyInNGN = convertCurrency(
              penaltyBase,
              (changePolicy.penalty_currency || 'GBP').toUpperCase(),
              'NGN'
            );
            const penaltyInUser = convertCurrency(
              penaltyInNGN,
              'NGN',
              userCurrencyForPdf.code
            );
            penalty = formatCurrency(penaltyInUser, {
              currency: userCurrencyForPdf,
            });
          } else {
            penalty = `${changePolicy.penalty_currency || ''}${changePolicy.penalty_amount}`;
          }
        }
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text('Flight Change Policy', 25, yPosition);
        yPosition += 6;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        const policyText1 = `Make changes to this flight up until the departure date (${penalty} will apply)`;
        // Use splitTextToSize with proper width (adjusted for box margin)
        const lines1 = doc.splitTextToSize(policyText1, pageWidth - 60);
        // Render each line properly
        lines1.forEach((line, index) => {
          doc.text(line, 25, yPosition + (index * 4));
        });
        yPosition += (lines1.length * 4) + 5;
      }
      
      // Order change policy
      if (conditions?.change_before_departure) {
        const changePolicy = conditions.change_before_departure;
        let penalty = 'a change penalty';
        if (changePolicy.penalty_amount) {
          const penaltyBase =
            parseFloat(changePolicy.penalty_amount) || 0;
          if (penaltyBase > 0) {
            const penaltyInNGN = convertCurrency(
              penaltyBase,
              (changePolicy.penalty_currency || 'GBP').toUpperCase(),
              'NGN'
            );
            const penaltyInUser = convertCurrency(
              penaltyInNGN,
              'NGN',
              userCurrencyForPdf.code
            );
            penalty = formatCurrency(penaltyInUser, {
              currency: userCurrencyForPdf,
            });
          } else {
            penalty = `${changePolicy.penalty_currency || ''}${changePolicy.penalty_amount}`;
          }
        }
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text('Order Change Policy', 25, yPosition);
        yPosition += 6;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        const policyText2 = `Make changes to this order up until the initial departure date (${penalty} will apply)`;
        // Use splitTextToSize with proper width (adjusted for box margin)
        const lines2 = doc.splitTextToSize(policyText2, pageWidth - 60);
        // Render each line properly
        lines2.forEach((line, index) => {
          doc.text(line, 25, yPosition + (index * 4));
        });
        yPosition += (lines2.length * 4) + 5;
      }
      
      // Refund policy
      if (conditions?.refund_before_departure) {
        const refundPolicy = conditions.refund_before_departure;
        let penalty = 'a refund penalty';
        if (refundPolicy.penalty_amount) {
          const penaltyBase =
            parseFloat(refundPolicy.penalty_amount) || 0;
          if (penaltyBase > 0) {
            const penaltyInNGN = convertCurrency(
              penaltyBase,
              (refundPolicy.penalty_currency || 'GBP').toUpperCase(),
              'NGN'
            );
            const penaltyInUser = convertCurrency(
              penaltyInNGN,
              'NGN',
              userCurrencyForPdf.code
            );
            penalty = formatCurrency(penaltyInUser, {
              currency: userCurrencyForPdf,
            });
          } else {
            penalty = `${refundPolicy.penalty_currency || ''}${refundPolicy.penalty_amount}`;
          }
        }
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text('Refund Policy', 25, yPosition);
        yPosition += 6;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        const policyText3 = `This order is refundable up until the initial departure date (${penalty} will apply)`;
        // Use splitTextToSize with proper width (adjusted for box margin)
        const lines3 = doc.splitTextToSize(policyText3, pageWidth - 60);
        // Render each line properly
        lines3.forEach((line, index) => {
          doc.text(line, 25, yPosition + (index * 4));
        });
        yPosition += (lines3.length * 4) + 5;
      }
      
      // Close policies card
      const policiesCardHeight = yPosition - policiesCardY;
      addBox(20, policiesCardY, pageWidth - 40, policiesCardHeight, null, borderGray);
      yPosition += 10;
      
      // BILLING SUMMARY - Beautiful design
      checkNewPage(80);
      
      addBox(15, yPosition, pageWidth - 30, 12, secondaryColor, null);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('BILLING SUMMARY', 25, yPosition + 8);
      doc.setTextColor(0, 0, 0);
      yPosition += 18;
      
      // Billing card
      addBox(20, yPosition, pageWidth - 40, 0, null, borderGray);
      const billingCardY = yPosition;
      yPosition += 10;
      
      // Helper function to add billing row with proper alignment
      const addBillingRow = (label, amount, isTotal = false) => {
        const fontSize = isTotal ? 12 : 10;
        const isBold = isTotal;
        
        // Check if new page needed
        checkNewPage(15);
        
        // Left side - label
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');
        if (isTotal) {
          doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        } else {
          doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        }
        doc.text(label, 25, yPosition);
        
        // Right side - amount (format without Naira symbol for better compatibility)
        let amountText = String(amount);
        // Replace Naira symbol with "NGN " for better PDF compatibility
        amountText = amountText.replace('₦', 'NGN ');
        
        if (isTotal) {
          doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        } else {
          doc.setTextColor(0, 0, 0);
        }
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');
        doc.text(amountText, pageWidth - 25, yPosition, { align: 'right' });
        
        doc.setTextColor(0, 0, 0);
        yPosition += isTotal ? 12 : 8;
      };
      
      // Base Fare - keep NGN as internal base, display in user currency
      let fareAmountNGN = 0;
      if (booking?.baseAmount) {
        // Already converted to NGN
        fareAmountNGN = parseFloat(booking.baseAmount);
      } else if (booking?.rawOrder?.base_amount) {
        // Convert from original currency to NGN
        const originalCurrency =
          booking?.rawOrder?.base_currency ||
          booking?.rawOrder?.total_currency ||
          'GBP';
        const baseAmount =
          parseFloat(booking.rawOrder.base_amount) || 0;
        fareAmountNGN = convertCurrency(
          baseAmount,
          originalCurrency,
          'NGN'
        );
      }
      
      if (fareAmountNGN > 0) {
        const fareInUser = convertCurrency(
          fareAmountNGN,
          'NGN',
          userCurrencyForPdf.code
        );
        const fareAmount = formatCurrency(fareInUser, {
          currency: userCurrencyForPdf,
        });
        addBillingRow('Fare', fareAmount);
      }
      
      // Taxes - keep NGN as internal base, display in user currency
      let taxAmountNGN = 0;
      if (booking?.taxAmount) {
        // Already converted to NGN
        taxAmountNGN = parseFloat(booking.taxAmount);
      } else if (booking?.rawOrder?.tax_amount) {
        // Convert from original currency to NGN
        const originalCurrency =
          booking?.rawOrder?.tax_currency ||
          booking?.rawOrder?.total_currency ||
          'GBP';
        const taxAmount =
          parseFloat(booking.rawOrder.tax_amount) || 0;
        taxAmountNGN = convertCurrency(
          taxAmount,
          originalCurrency,
          'NGN'
        );
      }
      
      if (taxAmountNGN > 0) {
        const taxInUser = convertCurrency(
          taxAmountNGN,
          'NGN',
          userCurrencyForPdf.code
        );
        const taxAmount = formatCurrency(taxInUser, {
          currency: userCurrencyForPdf,
        });
        addBillingRow('Fare taxes', taxAmount);
      }
      
      // Divider line
      checkNewPage(15);
      addLine(25, yPosition, pageWidth - 25, yPosition, borderGray, 1);
      yPosition += 8;
      
      // Total - keep NGN as internal base, display in user currency
      let totalAmountNGN = 0;
      if (booking?.totalAmount) {
        // Already converted to NGN
        totalAmountNGN = parseFloat(booking.totalAmount);
      } else if (booking?.rawOrder?.total_amount) {
        // Convert from original currency to NGN
        const originalCurrency =
          booking?.rawOrder?.total_currency || 'GBP';
        const totalBase =
          parseFloat(booking.rawOrder.total_amount) || 0;
        totalAmountNGN = convertCurrency(
          totalBase,
          originalCurrency,
          'NGN'
        );
      } else {
        // Fallback to calculateTotal
        const totalStr = calculateTotal().replace(/[₦,]/g, '');
        totalAmountNGN = parseFloat(totalStr) || 0;
      }
      
      const totalInUser = convertCurrency(
        totalAmountNGN,
        'NGN',
        userCurrencyForPdf.code
      );
      const totalAmount = formatCurrency(totalInUser, {
        currency: userCurrencyForPdf,
      });
      addBillingRow('Total', totalAmount, true);
      
      // Close billing card
      const billingCardHeight = yPosition - billingCardY;
      addBox(20, billingCardY, pageWidth - 40, billingCardHeight, null, borderGray);
      yPosition += 10;
      
      // Footer - Beautiful footer section
      checkNewPage(50);
      addBox(0, pageHeight - 40, pageWidth, 40, lightGray, null);
      addLine(0, pageHeight - 40, pageWidth, pageHeight - 40, borderGray, 1);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
      doc.text('Important Information', pageWidth / 2, pageHeight - 25, { align: 'center' });
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      const footerText = [
        '• Please arrive at the airport at least 2 hours before departure',
        '• Keep this ticket and a valid ID with you at all times',
        '• Check-in online 24 hours before your flight'
      ];
      footerText.forEach((text, idx) => {
        doc.text(text, pageWidth / 2, pageHeight - 15 + (idx * 5), { align: 'center' });
      });
      
      // Save the PDF
      const fileName = `Ticket_${primaryConfirmationCode}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      console.log('Comprehensive ticket PDF generated successfully:', fileName);
    } catch (error) {
      console.error('Error generating ticket PDF:', error);
      alert('Failed to generate ticket. Please try again or contact support.');
    }
  };

  const handleViewDashboard = () => {
    // Navigate to dashboard/my trips
    navigate('/dashboard');
  };

  const handleViewDetails = () => {
    // Navigate to trip details page
    navigate(`/trip/${flight.id}`, { 
      state: { 
        trip: {
          ...flight,
          flightNumber: `BA${flight.id}23`,
          aircraftType: flight.aircraftType || 'Boeing 777',
          terminal: 'Terminal 1',
          gate: 'Gate A12',
          boardingTime: flight.departureTime ? 
            `${String(parseInt(flight.departureTime.split(':')[0]) - 1).padStart(2, '0')}:${flight.departureTime.split(':')[1]}` : 
            '13:50',
          passengers: [
            {
              name: passengerData.fullName || (passengerData.firstName && passengerData.lastName ? `${passengerData.firstName} ${passengerData.lastName}` : 'Passenger'),
              type: 'Adult',
              seat: additionalOptions.seatSelection ? '12A' : null,
              ticketRef:
                ticketDocuments[0]?.ticketNumber ||
                (airlinePnr ? `PNR ${airlinePnr}` : `REF${bookingReference}`),
              nationality: 'Nigeria',
              gender: 'Male'
            }
          ],
          baggage: {
            carryOn: '1 piece (7kg)',
            checked: flight.travelClass === 'Business' ? '2 pieces (23kg each)' : '1 piece (23kg)'
          },
          meals: 'Available',
          price: {
            baseFare: flight.baseFare || '₦750,000',
            taxes: flight.taxes || '₦100,000',
            total: flight.total || flight.price
          },
          paymentMethod: 'Paystack',
          status: 'Confirmed',
          bookingReference: bookingReference
        }
      } 
    });
  };

  return (
    <div className="booking-success-page">
      <Header />
      
      {/* Success Message Section */}
      <section className="success-message-section">
        <div className="page-container">
          <div className="success-card">
            <div className="success-icon-wrapper">
              <div className="success-check">✓</div>
            </div>
            <h1 className="success-headline">Booking Confirmed!</h1>
            <p className="success-subtext">
              Your flight has been successfully booked. Your ticket and booking details are below.
            </p>
          </div>
        </div>
      </section>

      {/* Flight Summary / Ticket Details */}
      <section className="ticket-details-section">
        <div className="page-container">
          <div className="ticket-card">
            <div className="ticket-header">
              <div className="ticket-airline">
                <span className="ticket-logo">{flight?.airlineLogo || '✈️'}</span>
                <div>
                  <h3 className="ticket-airline-name">
                    {firstSegment?.marketing_carrier?.name || 
                     getAirlineName(firstSegment?.marketing_carrier?.iata_code) || 
                     flight?.airline || 
                     'Airline'}
                  </h3>
                  <p className="ticket-route">
                    {allSlices.length > 2 ? (
                      // Multi-city: show all routes
                      allSlices.map((slice, idx) => {
                        const seg = slice?.segments?.[0];
                        const lastSeg = slice?.segments?.[slice.segments.length - 1];
                        if (!seg || !lastSeg) return null;
                        return `${seg.origin?.iata_code || 'N/A'} → ${lastSeg.destination?.iata_code || 'N/A'}`;
                      }).filter(Boolean).join(' → ')
                    ) : returnSlice && returnFirstSegment ? (
                      // Round-trip
                      `${firstSegment?.origin?.iata_code || flight?.departureAirport || 'N/A'} ↔ ${returnLastSegment?.destination?.iata_code || flight?.arrivalAirport || 'N/A'}`
                    ) : (
                      // One-way
                      `${firstSegment?.origin?.iata_code || flight?.departureAirport || 'N/A'} → ${lastSegment?.destination?.iata_code || flight?.arrivalAirport || 'N/A'}`
                    )}
                  </p>
                </div>
              </div>
              <div className="booking-reference booking-reference-stack">
                <span className="reference-label">{confirmationLabel}</span>
                <span className="reference-code">{primaryConfirmationCode}</span>
                {showDuffelOrderSub && (
                  <div className="reference-secondary-block">
                    <span className="reference-label reference-label-muted">Duffel order ID</span>
                    <span className="reference-secondary-code">{duffelOrderId}</span>
                  </div>
                )}
                {!airlinePnr && isDuffelOrderId(bookingReference) && (
                  <p className="reference-hint">
                    The airline PNR (record locator) is not in the booking response yet. It may appear after the
                    carrier syncs your reservation.
                  </p>
                )}
                {extraCarrierPnrs.length > 0 && (
                  <ul className="reference-extra-list" aria-label="Additional carrier references">
                    {extraCarrierPnrs.map((row) => (
                      <li key={`${row.carrier || 'ref'}-${row.reference}`}>
                        {row.carrier ? (
                          <>
                            <span className="reference-extra-carrier">{row.carrier}</span>
                            <span className="reference-extra-sep"> · </span>
                          </>
                        ) : null}
                        <span className="reference-extra-ref">{row.reference}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {ticketDocuments.length > 0 && (
                  <div className="ticket-documents-block">
                    <span className="reference-label reference-label-muted">Ticket / document numbers</span>
                    <ul className="ticket-documents-list">
                      {ticketDocuments.map((row, idx) => (
                        <li key={`${row.ticketNumber}-${idx}`}>
                          <span className="ticket-doc-number">{row.ticketNumber}</span>
                          {row.documentType ? (
                            <span className="ticket-doc-type"> ({row.documentType})</span>
                          ) : null}
                          {row.passengersLabel ? (
                            <span className="ticket-doc-passengers"> — {row.passengersLabel}</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="ticket-body">
              {/* Multi-City Routes */}
              {allSlices.length > 2 && (
                <>
                  {allSlices.map((slice, sliceIndex) => {
                    const seg = slice?.segments?.[0];
                    const lastSeg = slice?.segments?.[slice.segments.length - 1];
                    if (!seg || !lastSeg) return null;
                    
                    const formatDuration = (duration) => {
                      if (!duration) return 'N/A';
                      if (typeof duration === 'number') {
                        return `${Math.floor(duration / 60)}h ${duration % 60}m`;
                      }
                      if (typeof duration === 'string' && duration.startsWith('PT')) {
                        const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
                        if (match) {
                          const hours = match[1] || '0';
                          const minutes = match[2] || '0';
                          if (hours === '0') return `${minutes}m`;
                          if (minutes === '0') return `${hours}h`;
                          return `${hours}h ${minutes}m`;
                        }
                      }
                      return duration;
                    };
                    
                    return (
                      <div key={sliceIndex} className="flight-leg-section" style={{ marginBottom: sliceIndex < allSlices.length - 1 ? '20px' : '0' }}>
                        <div className="leg-label">Route {sliceIndex + 1} of {allSlices.length}</div>
                        <div className="ticket-flight-info">
                          <div className="flight-time-block">
                            <div className="time-details">
                              <span className="time-value">{formatTime(seg.departing_at)}</span>
                              <span className="time-date">{formatDate(seg.departing_at)}</span>
                              <span className="airport-code">{seg.origin?.iata_code || 'N/A'}</span>
                              <span className="city-name">{seg.origin?.city_name || seg.origin?.name || 'N/A'}</span>
                            </div>
                          </div>

                          <div className="flight-connector">
                            <div className="connector-duration">{formatDuration(slice.duration)}</div>
                            <div className="connector-line">
                              <span className="connector-icon">✈️</span>
                            </div>
                            <div className="connector-class">
                              {slice.cabin_class_name || slice.cabin_class || flight?.travelClass || 'Economy'}
                            </div>
                          </div>

                          <div className="flight-time-block">
                            <div className="time-details">
                              <span className="time-value">{formatTime(lastSeg.arriving_at)}</span>
                              <span className="time-date">{formatDate(lastSeg.arriving_at)}</span>
                              <span className="airport-code">{lastSeg.destination?.iata_code || 'N/A'}</span>
                              <span className="city-name">{lastSeg.destination?.city_name || lastSeg.destination?.name || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {/* Outbound Flight (One-way or Round-trip) */}
              {allSlices.length <= 2 && (
                <div className="flight-leg-section outbound-leg-section">
                  <div className="leg-label">Outbound Flight</div>
                  <div className="ticket-flight-info">
                    <div className="flight-time-block">
                      <div className="time-details">
                        <span className="time-value">
                          {firstSegment?.departing_at ? formatTime(firstSegment.departing_at) : flight?.departureTime || 'N/A'}
                        </span>
                        <span className="time-date">
                          {firstSegment?.departing_at ? formatDate(firstSegment.departing_at) : flight?.departureDate || 'N/A'}
                        </span>
                        <span className="airport-code">
                          {firstSegment?.origin?.iata_code || flight?.departureAirport || 'N/A'}
                        </span>
                        <span className="city-name">
                          {firstSegment?.origin?.city_name || flight?.departureCity || firstSegment?.origin?.name || 'N/A'}
                        </span>
                      </div>
                    </div>

                    <div className="flight-connector">
                      <div className="connector-duration">
                        {firstSlice?.duration ? `${Math.floor(firstSlice.duration / 60)}h ${firstSlice.duration % 60}m` : flight?.duration || 'N/A'}
                      </div>
                      <div className="connector-line">
                        <span className="connector-icon">✈️</span>
                      </div>
                      <div className="connector-class">
                        {firstSlice?.cabin_class_name || firstSlice?.cabin_class || flight?.travelClass || 'Economy'}
                      </div>
                    </div>

                    <div className="flight-time-block">
                      <div className="time-details">
                        <span className="time-value">
                          {lastSegment?.arriving_at ? formatTime(lastSegment.arriving_at) : flight?.arrivalTime || 'N/A'}
                        </span>
                        <span className="time-date">
                          {lastSegment?.arriving_at ? formatDate(lastSegment.arriving_at) : flight?.arrivalDate || 'N/A'}
                        </span>
                        <span className="airport-code">
                          {lastSegment?.destination?.iata_code || flight?.arrivalAirport || 'N/A'}
                        </span>
                        <span className="city-name">
                          {lastSegment?.destination?.city_name || flight?.arrivalCity || lastSegment?.destination?.name || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Return Flight */}
              {returnSlice && returnFirstSegment && allSlices.length <= 2 && (
                <div className="flight-leg-section return-leg-section">
                  <div className="leg-label">Return Flight</div>
                  <div className="ticket-flight-info">
                    <div className="flight-time-block">
                      <div className="time-details">
                        <span className="time-value">
                          {returnFirstSegment?.departing_at ? formatTime(returnFirstSegment.departing_at) : 'N/A'}
                        </span>
                        <span className="time-date">
                          {returnFirstSegment?.departing_at ? formatDate(returnFirstSegment.departing_at) : 'N/A'}
                        </span>
                        <span className="airport-code">
                          {returnFirstSegment?.origin?.iata_code || 'N/A'}
                        </span>
                        <span className="city-name">
                          {returnFirstSegment?.origin?.city_name || returnFirstSegment?.origin?.name || 'N/A'}
                        </span>
                      </div>
                    </div>

                    <div className="flight-connector">
                      <div className="connector-duration">
                        {returnSlice?.duration ? `${Math.floor(returnSlice.duration / 60)}h ${returnSlice.duration % 60}m` : 'N/A'}
                      </div>
                      <div className="connector-line">
                        <span className="connector-icon">✈️</span>
                      </div>
                      <div className="connector-class">
                        {returnSlice?.cabin_class_name || returnSlice?.cabin_class || 'Economy'}
                      </div>
                    </div>

                    <div className="flight-time-block">
                      <div className="time-details">
                        <span className="time-value">
                          {returnLastSegment?.arriving_at ? formatTime(returnLastSegment.arriving_at) : 'N/A'}
                        </span>
                        <span className="time-date">
                          {returnLastSegment?.arriving_at ? formatDate(returnLastSegment.arriving_at) : 'N/A'}
                        </span>
                        <span className="airport-code">
                          {returnLastSegment?.destination?.iata_code || 'N/A'}
                        </span>
                        <span className="city-name">
                          {returnLastSegment?.destination?.city_name || returnLastSegment?.destination?.name || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="ticket-passenger-info">
                <h4 className="passenger-section-title">Passenger Details</h4>
                <div className="passenger-list">
                  {bookingPassengers.length > 0 ? (
                    bookingPassengers.map((passenger, index) => (
                      <div key={index} className="passenger-item">
                        <span className="passenger-name">
                          {passenger.given_name && passenger.family_name 
                            ? `${passenger.given_name} ${passenger.family_name}`
                            : passengerData.fullName || 
                              (passengerData.firstName && passengerData.lastName 
                                ? `${passengerData.firstName} ${passengerData.lastName}` 
                                : 'Passenger')}
                        </span>
                        <span className="passenger-type">
                          {passenger.type === 'adult' ? 'Adult' : 
                           passenger.type === 'child' ? 'Child' : 
                           passenger.type === 'infant_without_seat' ? 'Infant' : 
                           'Passenger'}
                        </span>
                        {additionalOptions.seatSelection && (
                          <span className="seat-number">Seat: {passenger.seat || '12A'}</span>
                        )}
                      </div>
                    ))
                  ) : (
                  <div className="passenger-item">
                      <span className="passenger-name">
                        {passengerData.fullName || 
                         (passengerData.firstName && passengerData.lastName 
                           ? `${passengerData.firstName} ${passengerData.lastName}` 
                           : 'Passenger')}
                      </span>
                    {additionalOptions.seatSelection && (
                      <span className="seat-number">Seat: 12A</span>
                    )}
                  </div>
                  )}
                </div>
              </div>

              <div className="ticket-price-breakdown">
                <h4 className="price-section-title">Price Breakdown</h4>
                <div className="price-details">
                  {/* Use converted amounts from booking (already in NGN) */}
                  {booking?.baseAmount !== undefined && booking?.taxAmount !== undefined ? (
                    <>
                      <div className="price-row">
                        <span className="price-label">Base Fare</span>
                        <span className="price-value">
                          {formatPrice(parseFloat(booking.baseAmount))}
                        </span>
                      </div>
                      <div className="price-row">
                        <span className="price-label">Taxes & Fees</span>
                        <span className="price-value">
                          {formatPrice(parseFloat(booking.taxAmount))}
                        </span>
                      </div>
                    </>
                  ) : booking?.rawOrder?.base_amount && booking?.rawOrder?.total_amount ? (
                    <>
                      {/* Fallback: convert from rawOrder if converted amounts not available */}
                      <div className="price-row">
                        <span className="price-label">Base Fare</span>
                        <span className="price-value">
                          {formatPrice(
                            convertCurrency(
                              parseFloat(booking.rawOrder.base_amount),
                              booking.rawOrder.base_currency || booking.rawOrder.total_currency || 'GBP',
                              'NGN'
                            )
                          )}
                        </span>
                      </div>
                      <div className="price-row">
                        <span className="price-label">Taxes & Fees</span>
                        <span className="price-value">
                          {formatPrice(
                            convertCurrency(
                              parseFloat(booking.rawOrder.total_amount) -
                                parseFloat(booking.rawOrder.base_amount || 0),
                              booking.rawOrder.total_currency || booking.rawOrder.base_currency || 'GBP',
                              'NGN'
                            )
                          )}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                  <div className="price-row">
                    <span className="price-label">Base Fare</span>
                        <span className="price-value">
                          {formatPrice(
                            parseFloat(
                              String(flight?.baseFare || '0').replace(/[^\d.]/g, '')
                            ) || 0
                          )}
                        </span>
                  </div>
                  <div className="price-row">
                    <span className="price-label">Taxes & Fees</span>
                        <span className="price-value">
                          {formatPrice(
                            parseFloat(
                              String(flight?.taxes || '0').replace(/[^\d.]/g, '')
                            ) || 0
                          )}
                        </span>
                  </div>
                    </>
                  )}
                  {additionalOptions.extraBaggage && (
                    <div className="price-row">
                      <span className="price-label">Extra Baggage</span>
                      <span className="price-value">{formatPrice(15000)}</span>
                    </div>
                  )}
                  {additionalOptions.seatSelection && (
                    <div className="price-row">
                      <span className="price-label">Seat Selection</span>
                      <span className="price-value">{formatPrice(5000)}</span>
                    </div>
                  )}
                  {additionalOptions.tripInsurance && (
                    <div className="price-row">
                      <span className="price-label">Trip Insurance</span>
                      <span className="price-value">{formatPrice(5000)}</span>
                    </div>
                  )}
                  <div className="price-divider"></div>
                  <div className="price-row total">
                    <span className="price-label">Total Paid</span>
                    <span className="price-value">
                      {formatPrice(getTotalPaidAmountNGN())}
                    </span>
                  </div>
                  {booking?.status && (
                    <div className="price-row">
                      <span className="price-label">Booking Status</span>
                      <span className="price-value" style={{ textTransform: 'capitalize' }}>
                        {booking.status}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="ticket-actions">
                <button className="download-ticket-button" onClick={handleDownloadTicket}>
                  Download Ticket
                </button>
                {isSignedIn && (
                  <button className="view-dashboard-button" onClick={handleViewDashboard}>
                    View My Trips
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BookingSuccessPage;

